import {
  appendWeeklyUsage,
  isDeviceActivationAllowed,
  mergePlotDevicePool,
  selectDailyPlotDevices,
} from '../domain/plotDeviceSelection.js';
import { SEEDED_PLOT_DEVICE_CATALOG } from '../domain/plotDeviceCatalog.js';
import type { OperatingSchedule, PlotDevice, WorldSnapshot } from '../domain/types.js';
import { localPartsFromWorldIso, operatingStatusForClock } from '../ui/worldRuntime/clock.js';
import type { PersistedRuntimeState } from '../ui/worldRuntime/types.js';

export function selectionSeed(snapshot: WorldSnapshot): string {
  return snapshot.generationContext?.generationSeed ?? snapshot.draftId;
}

export function resolveActiveDevices(snapshot: WorldSnapshot, deviceIds: string[]): PlotDevice[] {
  const pool = mergePlotDevicePool(SEEDED_PLOT_DEVICE_CATALOG, snapshot.devices);
  const byId = new Map(pool.map((d) => [d.deviceId, d]));
  return deviceIds.map((id) => byId.get(id)).filter(Boolean) as PlotDevice[];
}

function resetWeeklyUsageIfNeeded(
  state: PersistedRuntimeState,
  weekKey: string,
): PersistedRuntimeState {
  if (state.deviceWeekKey === weekKey) return state;
  return { ...state, deviceWeekKey: weekKey, usedDevicesThisWeek: [] };
}

/** Daily pick: ≤2 devices, 7-day no-reuse, none while closed (notes §12). */
export function ensureDailyDevices(
  snapshot: WorldSnapshot,
  state: PersistedRuntimeState,
  inWorldDate: string,
  weekKey: string,
): PersistedRuntimeState {
  const reset = resetWeeklyUsageIfNeeded(state, weekKey);
  const status = operatingStatusForClock(snapshot.schedule, reset.clock.inWorldIso).status;
  if (status === 'closed') {
    return {
      ...reset,
      currentInWorldDate: inWorldDate,
      activeDeviceIds: [],
    };
  }
  if (reset.currentInWorldDate === inWorldDate && reset.activeDeviceIds.length > 0) {
    return reset;
  }
  const picked = selectDailyPlotDevices({
    worldId: snapshot.worldId,
    inWorldDate,
    selectionSeed: selectionSeed(snapshot),
    usedThisWeek: reset.usedDevicesThisWeek,
    worldDevices: snapshot.devices,
  });
  const used = appendWeeklyUsage(reset.usedDevicesThisWeek, picked.deviceIds);
  return {
    ...reset,
    currentInWorldDate: inWorldDate,
    activeDeviceIds: picked.deviceIds,
    usedDevicesThisWeek: used,
  };
}

/** First/last 10 live minutes — activation only, never surfaced in UI. */
export function activatableDeviceIds(
  schedule: OperatingSchedule,
  inWorldIso: string,
  deviceIds: readonly string[],
): string[] {
  const local = localPartsFromWorldIso(inWorldIso, schedule);
  if (!isDeviceActivationAllowed(schedule, local)) return [];
  return [...deviceIds];
}
